// ============================================================
// Utilidades de fechas y horas con soporte de zona horaria
// ============================================================
//
// ¿Por qué existen estas funciones?
// En JavaScript, una fecha guardada como UTC puede mostrarse con un día menos
// dependiendo de la zona horaria del navegador. Ejemplo:
//
//   2026-04-29T00:00:00.000Z
//
// En Colombia puede verse como 28 de abril en la noche si se usa new Date()
// directamente para pintar una fecha calendario.
//
// Estas funciones ayudan a trabajar siempre con la zona horaria correcta,
// por ejemplo: "America/Bogota".

const ZONA_HORARIA_DEFECTO = "America/Bogota";

// Extrae año, mes y día de un Date según una zona horaria específica.
// Retorna números: { y: 2026, m: 4, d: 29 }
export function partesYMDEnZona(date, timeZone = ZONA_HORARIA_DEFECTO) {
  if (!date) return { y: null, m: null, d: null };

  const fecha = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(fecha.getTime())) {
    return { y: null, m: null, d: null };
  }

  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // formatToParts devuelve las piezas de la fecha separadas.
  // Ejemplo: [{ type: "year", value: "2026" }, { type: "month", value: "04" }, ...]
  const partes = dtf.formatToParts(fecha);

  const obtenerParte = (tipo) => partes.find((parte) => parte.type === tipo)?.value;

  return {
    y: Number(obtenerParte("year")),
    m: Number(obtenerParte("month")),
    d: Number(obtenerParte("day")),
  };
}

// Convierte una fecha a formato YYYY-MM-DD respetando la zona horaria.
// Útil para comparar días de calendario sin que se corran por UTC.
export function ymdEnZona(date, timeZone = ZONA_HORARIA_DEFECTO) {
  if (!date) return "";

  const { y, m, d } = partesYMDEnZona(date, timeZone);

  if (!y || !m || !d) return "";

  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

// Retorna el año y mes actual según una zona horaria.
// m viene de 1 a 12.
//
// Nota importante:
// Este archivo es compartido por muchos componentes del frontend, por eso debe mantenerse "puro".
// No debe consultar el backend ni depender directamente de Direccionamiento/Outlet context.
// Si un componente necesita respetar la fecha de pruebas del backend, debe pasarla como fechaBase.
// Si no se pasa fechaBase, se usa la fecha real del navegador con new Date().
export function obtenerYMActualEnZona(timeZone = ZONA_HORARIA_DEFECTO, fechaBase = new Date()) {
  const fecha = fechaBase instanceof Date ? fechaBase : new Date(fechaBase);
  const fechaValida = Number.isNaN(fecha.getTime()) ? new Date() : fecha;
  const { y, m } = partesYMDEnZona(fechaValida, timeZone);

  return { y, m };
}

// Retorna el día de la semana según una zona horaria.
// 1 = Lunes, 2 = Martes, ..., 7 = Domingo.
export function weekday1a7EnZona(date, timeZone = ZONA_HORARIA_DEFECTO) {
  if (!date) return null;

  const fecha = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(fecha.getTime())) return null;

  const diaTexto = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
  }).format(fecha);

  const mapaDias = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
  };

  return mapaDias[diaTexto] || null;
}

// Retorna la hora en formato HH:mm según una zona horaria.
// Ejemplo: "06:00".
export function hmEnZona(date, timeZone = ZONA_HORARIA_DEFECTO) {
  if (!date) return "";

  const fecha = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(fecha.getTime())) return "";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(fecha);
}

// Formatea una fecha calendario sin mover el día por zona horaria.
// Úsala para campos como reserva.fecha cuando representa el día elegido por el usuario.
// Ejemplo: "2026-04-29T00:00:00.000Z" debe mostrarse como 29 de abril, no 28.
export function formatearFechaCalendario(fecha, timeZone = ZONA_HORARIA_DEFECTO, locale = "es-CO") {
  if (!fecha) return "";

  const textoFecha = fecha instanceof Date ? fecha.toISOString() : String(fecha);
  const ymd = textoFecha.slice(0, 10);

  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";

  const [year, month, day] = ymd.split("-").map(Number);

  // Usamos mediodía UTC para evitar que la fecha se desplace al día anterior
  // al formatearse en zonas horarias negativas como America/Bogota.
  const fechaUtcSegura = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

  return fechaUtcSegura.toLocaleDateString(locale, {
    timeZone,
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
}

// Formatea una fecha/hora real respetando la zona horaria.
// Úsala para campos como inicio, fin, createdAt, updatedAt, etc.
export function formatearFechaHora(fecha, timeZone = ZONA_HORARIA_DEFECTO, locale = "es-CO") {
  if (!fecha) return "";

  const d = fecha instanceof Date ? fecha : new Date(fecha);

  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleString(locale, {
    timeZone,
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}




// Convierte una fecha calendario + hora local de una zona horaria a un Date UTC real.
// Ejemplo:
// fechaCalendario: 2026-05-17
// hora: "03:00"
// timeZone: "America/Bogota"
// Resultado: Date equivalente a 2026-05-17 03:00 en Colombia, pero en UTC.
export function fechaHoraEnZonaAUTC(fechaCalendario, hora, timeZone = ZONA_HORARIA_DEFECTO) {
  if (!fechaCalendario || !hora) return null;

  const [hh, mm] = String(hora).split(":").map(Number);

  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;

  const partes = partesYMDEnZona(fechaCalendario, timeZone);

  if (!partes?.y || !partes?.m || !partes?.d) return null;

  const utcAproximado = Date.UTC(partes.y, partes.m - 1, partes.d, hh, mm, 0);

  const partesZona = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(utcAproximado)).reduce((acc, part) => {
    if (part.type !== "literal") acc[part.type] = part.value;
    return acc;
  }, {});

  const utcEquivalenteZona = Date.UTC(
    Number(partesZona.year),
    Number(partesZona.month) - 1,
    Number(partesZona.day),
    Number(partesZona.hour),
    Number(partesZona.minute),
    Number(partesZona.second)
  );

  const offsetMinutos = (utcEquivalenteZona - utcAproximado) / 60000;

  return new Date(utcAproximado - offsetMinutos * 60000);
}








export default {
  partesYMDEnZona,
  ymdEnZona,
  obtenerYMActualEnZona,
  weekday1a7EnZona,
  hmEnZona,
  formatearFechaCalendario,
  formatearFechaHora,
  fechaHoraEnZonaAUTC
};